import {
  type PillMonikerSegment,
  splitPillMonikers,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitPillMonikers';

export type WorkspaceAgentMonikerCopyLabelSegment = Extract<PillMonikerSegment, { type: 'pill' }>;

const FENCED_CODE_BLOCK_PATTERN = /```[\s\S]*?```/g;
const INLINE_CODE_PATTERN = /`[^`\n]+`/g;

const replaceMonikersInPlainSegment = (
  segment: string,
  resolveMonikerLabel: (monikerSegment: WorkspaceAgentMonikerCopyLabelSegment) => string,
): string =>
  splitPillMonikers(segment)
    .map((part) => {
      if (part.type === 'pill') {
        return resolveMonikerLabel(part);
      }

      if (part.type === 'text') {
        return part.value;
      }

      return part.raw;
    })
    .join('');

const replaceMonikersOutsideInlineCode = (
  segment: string,
  resolveMonikerLabel: (monikerSegment: WorkspaceAgentMonikerCopyLabelSegment) => string,
): string => {
  const pieces: string[] = [];
  const inlinePattern = new RegExp(INLINE_CODE_PATTERN.source, 'g');
  let lastIndex = 0;

  for (const match of segment.matchAll(inlinePattern)) {
    const start = match.index;

    if (start === undefined) {
      continue;
    }

    if (start > lastIndex) {
      pieces.push(replaceMonikersInPlainSegment(segment.slice(lastIndex, start), resolveMonikerLabel));
    }

    pieces.push(match[0]);
    lastIndex = start + match[0].length;
  }

  pieces.push(replaceMonikersInPlainSegment(segment.slice(lastIndex), resolveMonikerLabel));

  return pieces.join('');
};

/** Resolves `@[pill:<id>]` to copy labels without touching fenced or inline code (matches remark pill rules). */
export const replaceWorkspaceAgentPillMonikersOutsideCode = (
  markdown: string,
  resolveMonikerLabel: (monikerSegment: WorkspaceAgentMonikerCopyLabelSegment) => string,
): string => {
  const pieces: string[] = [];
  const fencedPattern = new RegExp(FENCED_CODE_BLOCK_PATTERN.source, 'g');
  let lastIndex = 0;

  for (const match of markdown.matchAll(fencedPattern)) {
    const start = match.index;

    if (start === undefined) {
      continue;
    }

    if (start > lastIndex) {
      pieces.push(replaceMonikersOutsideInlineCode(markdown.slice(lastIndex, start), resolveMonikerLabel));
    }

    pieces.push(match[0]);
    lastIndex = start + match[0].length;
  }

  pieces.push(replaceMonikersOutsideInlineCode(markdown.slice(lastIndex), resolveMonikerLabel));

  return pieces.join('');
};
